import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import {
  Alert,
  Badge,
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import { Breadcrumbs } from "../../../AbstractElements";
import { fetchBranches } from "../../../api/users";
import {
  approveExpense,
  createExpense,
  fetchCostCenters,
  fetchExpenseCategories,
  fetchExpenses,
  fetchVendors,
  payExpense,
  rejectExpense,
  submitExpense,
} from "../../../api/accounting";
import { formatDate, formatTZS } from "../../../utils/format";
import { useConfirmDialog } from "../Common/ConfirmDialogContext";
import { DEFAULT_TABLE_CUSTOM_STYLES } from "../Common/credoraTableUtils";
import { formatApiError, runCrud, runLoad } from "../Common/credoraToast";
import { ACCOUNTING_SECTION } from "./accountingPlaceholders";
import AccountingIconActions from "./AccountingIconActions";
import { EXPENSE_STATUS_META, todayIsoDate, useLatestRef } from "./accountingUtils";

const ExpensesPage = () => {
  const { confirm, prompt } = useConfirmDialog();
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [categories, setCategories] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    category_id: "",
    vendor_id: "",
    cost_center_id: "",
    expense_date: todayIsoDate(),
    amount: "",
    description: "",
  });

  useEffect(() => {
    fetchBranches(true).then((list) => {
      setBranches(list || []);
      if (list?.length) setBranchId(String(list[0].id));
    });
  }, []);

  const loadMeta = useCallback(async () => {
    if (!branchId) return;
    const [c, v, cc] = await Promise.all([
      fetchExpenseCategories(branchId),
      fetchVendors(branchId),
      fetchCostCenters(branchId),
    ]);
    setCategories(c || []);
    setVendors(v || []);
    setCostCenters(cc || []);
  }, [branchId]);

  const load = useCallback(() => {
    if (!branchId) return Promise.resolve();
    setLoading(true);
    setError("");
    const params = { branch_id: branchId };
    if (statusFilter) params.status = statusFilter;
    return runLoad(
      () => Promise.all([fetchExpenses(params).then(setRows), loadMeta()]),
      { errorMessage: "Failed to load expenses." }
    )
      .catch((err) => setError(formatApiError(err, "Failed to load expenses.")))
      .finally(() => setLoading(false));
  }, [branchId, statusFilter, loadMeta]);

  useEffect(() => {
    load();
  }, [load]);

  const loadRef = useLatestRef(load);

  const runAction = useCallback(
    (row, action) => {
      const labels = {
        submit: { title: "Submit expense?", label: "Submit", msg: "Expense will move to submitted status." },
        approve: { title: "Approve expense?", label: "Approve", msg: "Expense will be ready for payment." },
        pay: { title: "Mark as paid?", label: "Pay", msg: "Payment may auto-post a journal entry per settings." },
      };
      const meta = labels[action];
      confirm({
        title: meta.title,
        message: meta.msg,
        confirmLabel: meta.label,
        tone: meta.tone || "primary",
        successMessage: `Expense ${action === "pay" ? "paid" : action + "ted"}.`,
        onConfirm: async () => {
          if (action === "submit") await submitExpense(row.id);
          else if (action === "approve") await approveExpense(row.id);
          else if (action === "pay") await payExpense(row.id);
          await loadRef.current();
        },
      });
    },
    [confirm]
  );

  const handleReject = useCallback(
    (row) => {
      prompt({
        title: "Reject expense",
        message: `Reject expense ${row.reference}?`,
        label: "Rejection reason",
        placeholder: "e.g. Missing receipt or invalid category",
        confirmLabel: "Reject",
        cancelLabel: "Cancel",
        tone: "danger",
        required: true,
        multiline: true,
        rows: 3,
        onConfirm: async (reason) => {
          await rejectExpense(row.id, reason.trim());
          await loadRef.current();
        },
        successMessage: "Expense rejected.",
      });
    },
    [prompt]
  );

  const columns = useMemo(
    () => [
      { name: "Reference", selector: (r) => r.reference, width: "120px" },
      {
        name: "Date",
        selector: (r) => r.expense_date,
        cell: (r) => formatDate(r.expense_date),
        width: "110px",
      },
      { name: "Description", selector: (r) => r.description, grow: 2 },
      { name: "Category", selector: (r) => r.category_name || "—", width: "140px" },
      { name: "Vendor", selector: (r) => r.vendor_name || "—", width: "140px" },
      {
        name: "Amount",
        selector: (r) => Number(r.amount),
        cell: (r) => formatTZS(r.amount),
        right: true,
        width: "130px",
      },
      {
        name: "Status",
        cell: (r) => {
          const meta = EXPENSE_STATUS_META[r.status] || { label: r.status, color: "secondary" };
          return (
            <span title={r.rejection_reason || undefined}>
              <Badge color={meta.color}>{meta.label}</Badge>
            </span>
          );
        },
        width: "110px",
      },
      {
        name: "Rejection reason",
        selector: (r) => r.rejection_reason || "—",
        grow: 1,
        wrap: true,
      },
      {
        name: "Actions",
        cell: (r) => (
          <AccountingIconActions
            actions={[
              r.status === "draft" && {
                icon: "export",
                label: "Submit",
                onClick: () => runAction(r, "submit"),
                variant: "primary",
              },
              r.status === "submitted" && {
                icon: "check",
                label: "Approve",
                onClick: () => runAction(r, "approve"),
                variant: "success",
              },
              r.status === "submitted" && {
                icon: "failed",
                label: "Reject",
                onClick: () => handleReject(r),
                variant: "danger",
              },
              r.status === "approved" && {
                icon: "payment",
                label: "Pay",
                onClick: () => runAction(r, "pay"),
                variant: "primary",
              },
            ].filter(Boolean)}
          />
        ),
        width: "120px",
        ignoreRowClick: true,
      },
    ],
    [runAction, handleReject]
  );

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await runCrud(
        () =>
          createExpense({
            branch_id: branchId,
            category_id: form.category_id,
            vendor_id: form.vendor_id || null,
            cost_center_id: form.cost_center_id || null,
            expense_date: form.expense_date,
            amount: Number(form.amount),
            description: form.description,
          }),
        { successMessage: "Expense recorded." }
      );
      setModalOpen(false);
      setForm({
        category_id: "",
        vendor_id: "",
        cost_center_id: "",
        expense_date: todayIsoDate(),
        amount: "",
        description: "",
      });
      load();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Fragment>
      <Breadcrumbs mainTitle={ACCOUNTING_SECTION} parent={ACCOUNTING_SECTION} title="Expenses" />
      <div className="container-fluid">
        {error && <Alert color="danger">{error}</Alert>}
        <div className="card">
          <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
            <h5 className="mb-0">Expense register</h5>
            <div className="d-flex flex-wrap gap-2">
              <Input type="select" bsSize="sm" style={{ width: "160px" }} value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </Input>
              <Input type="select" bsSize="sm" style={{ width: "130px" }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="">All status</option>
                {Object.entries(EXPENSE_STATUS_META).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </Input>
              <Button color="primary" size="sm" onClick={() => setModalOpen(true)}>New expense</Button>
            </div>
          </div>
          <div className="card-body p-0">
            {loading ? (
              <div className="text-center py-5"><Spinner color="primary" /></div>
            ) : (
              <DataTable columns={columns} data={rows} customStyles={DEFAULT_TABLE_CUSTOM_STYLES} pagination />
            )}
          </div>
        </div>

        <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} size="lg">
          <Form onSubmit={handleCreate}>
            <ModalHeader toggle={() => setModalOpen(false)}>New expense</ModalHeader>
            <ModalBody>
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label>Category</Label>
                    <Input type="select" required value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })}>
                      <option value="">Select category</option>
                      {categories.filter((c) => c.is_active).map((c) => (
                        <option key={c.id} value={c.id}>{c.code} — {c.name}</option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label>Vendor (optional)</Label>
                    <Input type="select" value={form.vendor_id} onChange={(e) => setForm({ ...form, vendor_id: e.target.value })}>
                      <option value="">None</option>
                      {vendors.filter((v) => v.is_active).map((v) => (
                        <option key={v.id} value={v.id}>{v.name}</option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label>Cost center (optional)</Label>
                    <Input type="select" value={form.cost_center_id} onChange={(e) => setForm({ ...form, cost_center_id: e.target.value })}>
                      <option value="">None</option>
                      {costCenters.filter((c) => c.is_active).map((c) => (
                        <option key={c.id} value={c.id}>{c.code} — {c.name}</option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label>Date</Label>
                    <Input type="date" required value={form.expense_date} onChange={(e) => setForm({ ...form, expense_date: e.target.value })} />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label>Amount (TZS)</Label>
                    <Input type="number" min="0.01" step="0.01" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
                  </FormGroup>
                </Col>
                <Col md="12">
                  <FormGroup>
                    <Label>Description</Label>
                    <Input required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                  </FormGroup>
                </Col>
              </Row>
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" type="button" onClick={() => setModalOpen(false)}>Cancel</Button>
              <Button color="primary" type="submit" disabled={saving}>{saving ? "Saving…" : "Save draft"}</Button>
            </ModalFooter>
          </Form>
        </Modal>
      </div>
    </Fragment>
  );
};

export default ExpensesPage;
